import { Link } from 'react-router-dom';
import { useFollowedIssues, useFollowMutation } from '@/hooks/useFollows.js';
import type { FollowedIssueSummary } from '@/hooks/useFollows.js';
import { PageHeader } from '@/components/PageHeader.js';
import { LoadingState, ErrorState, EmptyState } from '@/components/PageState.js';
import { StatusBadge } from '@/components/StatusBadge.js';
import { PriorityIcon } from '@/components/PriorityIcon.js';
import { IssueTypeIcon } from '@/components/IssueTypeIcon.js';
import { LinkIndicator } from '@/components/LinkIndicator.js';
import { Avatar } from '@/components/Avatar.js';

export function FollowedIssues() {
  const { data: issues, isLoading, error } = useFollowedIssues();

  if (isLoading) return <LoadingState label="Loading followed issues..." />;
  if (error) return <ErrorState message="Failed to load followed issues" />;

  return (
    <div>
      <PageHeader title="Following" meta={issues && issues.length > 0 ? `${issues.length}` : undefined} />
      <p className="-mt-3 mb-5 text-sm text-gray-500">
        You&apos;ll get a Slack DM when any of these issues change.
      </p>

      {!issues || issues.length === 0 ? (
        <EmptyState title="You're not following any issues yet">
          Open an issue and click &quot;Follow&quot; to get notified of changes.
        </EmptyState>
      ) : (
        <div className="overflow-x-auto rounded-lg border bg-white">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b bg-gray-50/80 text-left text-xs font-semibold text-gray-500">
                <th className="w-32 px-3 py-2">Key</th>
                <th className="px-3 py-2">Summary</th>
                <th className="w-36 px-3 py-2">Status</th>
                <th className="hidden w-20 px-3 py-2 text-center md:table-cell">Priority</th>
                <th className="hidden w-44 px-3 py-2 lg:table-cell">Assignee</th>
                <th className="w-24 px-3 py-2"><span className="sr-only">Actions</span></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {issues.map((issue) => (
                <FollowedIssueRow key={issue.issueKey} issue={issue} />
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function FollowedIssueRow({ issue }: { issue: FollowedIssueSummary }) {
  const { unfollow } = useFollowMutation(issue.issueKey);
  const category = issue.statusCategory ?? 'undefined';

  return (
    <tr className="group hover:bg-blue-50/40">
      <td className="whitespace-nowrap px-3 py-2">
        <div className="flex items-center gap-2">
          {issue.issuetype && <IssueTypeIcon name={issue.issuetype} />}
          <Link to={`/issues/${issue.issueKey}`} className="font-medium text-blue-600 hover:underline">
            {issue.issueKey}
          </Link>
        </div>
      </td>
      <td className="max-w-0 px-3 py-2">
        <div className="flex items-center gap-2">
          <Link to={`/issues/${issue.issueKey}`} className="truncate text-gray-900 hover:text-blue-700" title={issue.summary}>
            {issue.summary}
          </Link>
          <LinkIndicator links={issue.links} />
        </div>
      </td>
      <td className="px-3 py-2">
        <StatusBadge status={{ name: issue.status, category }} />
      </td>
      <td className="hidden px-3 py-2 md:table-cell">
        <div className="flex justify-center">
          <PriorityIcon name={issue.priority} iconUrl={issue.priorityIconUrl} />
        </div>
      </td>
      <td className="hidden px-3 py-2 lg:table-cell">
        {issue.assignee ? (
          <div className="flex min-w-0 items-center gap-2">
            <Avatar name={issue.assignee.displayName} avatarUrl={issue.assignee.avatarUrl} />
            <span className="truncate text-gray-700">{issue.assignee.displayName}</span>
          </div>
        ) : (
          <span className="text-gray-400">Unassigned</span>
        )}
      </td>
      <td className="px-3 py-2 text-right">
        <button
          onClick={() => unfollow.mutate()}
          disabled={unfollow.isPending}
          className="rounded-md px-2 py-1 text-xs font-medium text-gray-500 hover:bg-red-50 hover:text-red-700 disabled:opacity-50"
        >
          {unfollow.isPending ? 'Removing...' : 'Unfollow'}
        </button>
      </td>
    </tr>
  );
}
